import type { BusinessDayResource, SaleResource } from '@repo/api-client';

import { isNotFound } from '@/api/failure';

import { formatBusinessDate } from '../tickets/business-date';

/**
 * The trading day a sale belongs to: the date somebody names, and the id the
 * filter wants.
 *
 * ## The date is the input; the id is the server's answer
 *
 * `GET /v1/sales` narrows the trading day by **`businessDayId`** and not by a
 * calendar date, which is where it differs from `GET /v1/tickets`.
 * `ListSalesQuery` argues the case and is right: a business day is one row per
 * `(outlet, date)` that Day Close opens and closes (R-415), so a date alone
 * names as many days as there are outlets, and reconciling one outlet's takings
 * against one drawer is a question about one of those rows.
 *
 * What used to be missing was the other end. Until #228 nothing in the shipped
 * document published a business day, so there was no request that turned a day
 * somebody picked into the id the filter wants, and the interim was to use each
 * row's own `businessDayId` as a handle. **That gap is closed**: `GET
 * /v1/outlets/{outletId}/business-days/{businessDate}` resolves an outlet and a
 * `YYYY-MM-DD` to the day in one request, so the screen takes a date and the
 * server answers with the id.
 *
 * This is deliberately **one** source and not two. The id and the date held
 * apart — one set from a row, the other from a picker — is a screen quietly
 * claiming to show a day it is not showing, and nothing would catch it because
 * both halves would be individually valid. So a row's trading day now *fills the
 * picker* (`tradingDayHandleOf`) rather than carrying an id of its own, and every
 * active filter has been through the same lookup.
 *
 * ### What that trades, stated rather than hidden
 *
 * A row hands over a date derived from its `recordedAt`, and the server resolves
 * a sale's day from its own clock in `Asia/Kolkata` at record time (R-415).
 * `findOrOpen` and `recordedAt` are two reads of that clock inside one
 * transaction, so a sale recorded in the microseconds that straddle midnight can
 * derive the adjacent date — and clicking such a row would filter to a day that
 * does not contain it. Under the old handle the id was exact and this could not
 * happen.
 *
 * It is the right trade at this size. The window is microseconds wide, the
 * failure is visible rather than silent (the row you clicked is not in the
 * answer), and what it buys is that the label, the state and the id all come
 * from the same response — so the chip can no longer disagree with the rows
 * beneath it, which was a permanent hazard rather than a microsecond one.
 *
 * ## The label is no longer derived
 *
 * It is `businessDate` exactly as the server sent it, rendered by
 * `formatBusinessDate`. Nothing here decides what day a set of sales is.
 */

/**
 * The park's calendar zone (R-006).
 *
 * Fixed, matching `BUSINESS_TIME_ZONE` on the API: the scope assumes one site.
 * Stated explicitly here and **not** left to the device, which is the difference
 * between this and `lib/instant.ts` — that one renders an instant for whoever is
 * standing where it happened, and this one names a *business day*, which is a
 * fact about the park rather than about the reader. A browser set to another
 * zone must not relabel the park's trading day.
 */
const BUSINESS_TIME_ZONE = 'Asia/Kolkata';

/** `13 Aug 2026` — the day, with no time in it, because a trading day has none. */
const TRADING_DAY = new Intl.DateTimeFormat('en-IN', {
  dateStyle: 'medium',
  timeZone: BUSINESS_TIME_ZONE,
});

/**
 * Which trading day a sale landed in, as a person reads it.
 *
 * `new Date(iso)` parses a value the **server** sent. It reads no clock —
 * `lib/clock.ts` governs that, and nothing here asks what day it is now, which
 * is the same reason the tickets filter has no "today" and this picker has no
 * future bound.
 */
export const formatTradingDay = (recordedAt: string): string =>
  TRADING_DAY.format(new Date(recordedAt));

/**
 * The same instant as `YYYY-MM-DD`, which is the shape the lookup route's path
 * takes.
 *
 * Built from `formatToParts` in the park's zone rather than from `toISOString`,
 * which answers UTC and is a day out for every evening after 18:30 UTC — 00:00
 * in Munnar. The parts are already zero-padded by the `2-digit` options, and
 * `en-GB` is named only to keep the numbering system Latin on a device set to
 * another; the parts are read by type and never by position.
 */
const BUSINESS_DATE_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function businessDateOf(recordedAt: string): string {
  const parts = BUSINESS_DATE_PARTS.formatToParts(new Date(recordedAt));
  const part = (type: 'year' | 'month' | 'day') =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';

  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * The trading day filter, as the screen holds it once the server has resolved
 * one.
 *
 * Every field comes off the same `BusinessDayResource`, so the id, the date the
 * picker shows and the words the chip reads cannot drift from one another.
 */
export interface TradingDayFilter {
  /** `businessDayId`, exactly as the server sent it. Never rendered. */
  readonly id: string;

  /** What the chip reads — the server's own date, not a derived one. */
  readonly label: string;

  /** `YYYY-MM-DD`, which is what the picker is set to. */
  readonly date: string;

  /**
   * Whether the day is open now.
   *
   * A closed day may be reopened (CL-603), so this is not a one-way transition
   * and nothing may treat `CLOSED` as final.
   */
  readonly state: BusinessDayResource['state'];

  /**
   * How many times the day has been reopened; 0 for most.
   *
   * Shown because without it a day closed and reopened reads exactly like one
   * never closed (CL-603), and a reconciliation against a drawer wants to know.
   */
  readonly reopenCount: number;
}

/**
 * The outlet and the date a row stands for — what clicking a row's trading day
 * fills the picker with.
 *
 * Both, never just the date: a business day is one outlet's day, so a date on
 * its own names as many days as there are outlets.
 */
export const tradingDayHandleOf = (
  sale: SaleResource,
): { readonly outletId: string; readonly date: string } => ({
  outletId: sale.outletId,
  date: businessDateOf(sale.recordedAt),
});

/** The filter a resolved day stands for. */
export const tradingDayOfBusinessDay = (day: BusinessDayResource): TradingDayFilter => ({
  id: day.id,
  label: formatBusinessDate(day.businessDate),
  date: day.businessDate,
  state: day.state,
  reopenCount: day.reopenCount,
});

/**
 * Where the chosen day has got to.
 *
 * One value rather than a date beside a loading flag beside an error, because
 * the states are exclusive and the list's query depends on which one holds:
 * only `resolved` may put a `businessDayId` on the wire.
 */
export type DayResolution =
  | { readonly kind: 'every' }
  | { readonly kind: 'resolving'; readonly date: string }
  | { readonly kind: 'resolved'; readonly date: string; readonly day: TradingDayFilter }
  | { readonly kind: 'absent'; readonly date: string }
  | { readonly kind: 'failed'; readonly date: string; readonly error: unknown };

/**
 * The id to filter by, or `undefined` when there is not one yet.
 *
 * The guard that keeps a half-applied filter off the wire. A query that simply
 * omitted `businessDayId` while the lookup was in flight would answer for
 * **every** day and read exactly as though the filter had been cleared — so the
 * caller holds the list instead of asking a question it does not mean.
 */
export const businessDayIdOf = (resolution: DayResolution): string | undefined =>
  resolution.kind === 'resolved' ? resolution.day.id : undefined;

/** The date a resolution concerns, or `''` when no day is chosen. */
export const dateOfResolution = (resolution: DayResolution): string =>
  resolution.kind === 'every' ? '' : resolution.date;

/**
 * What a failed lookup means.
 *
 * A 404 is an **answer**, not a fault: that outlet genuinely has no trading day
 * on that date, which is what a day the outlet did not trade looks like — a day
 * is opened by the first record of it and never by a read (FR-605). Worded as an
 * empty result rather than as an error, and offered no retry, because retrying
 * will say the same thing.
 *
 * Everything else — a refusal, an outage, a malformed date that got past
 * `isCalendarDate` — stays a failure the screen can offer a retry for.
 */
export const resolutionAfterFailure = (date: string, error: unknown): DayResolution =>
  isNotFound(error) ? { kind: 'absent', date } : { kind: 'failed', date, error };
